import {
  EntityLinks,
  Goal,
  GoalGroupBy,
  GoalProgressCount,
  GoalRoadmapSortingKeys,
  GoalRoadmapSortingOptions,
  GoalSortingMode,
  GoalTreeIndex,
  GroupByMode,
  ID,
  LifeArea,
} from '@griply/types';

import { buildParentToChildrenMap } from './goals/get-sub-goals';
import { hasGoalParent } from './goals/has-goal-parent';
import { isActiveLifeArea } from './is-active-life-area';
import { sortGoals } from './sort-goals';

// A hierarchy never legitimately nests this deep; corrupt parentIds are the only way to reach it.
const MAX_GOAL_DEPTH = 1000;

export type BuildSortedGoalTreeOptions<
  TGoal extends Goal = Goal,
  TLifeArea extends LifeArea = LifeArea,
> = {
  goals: TGoal[];
  sortedLifeAreas: TLifeArea[];
  goalGroupBy?: GoalGroupBy;
  goalSortingMode?: GoalSortingMode;
  goalRoadmapSorting?: Partial<Record<GoalRoadmapSortingOptions, ID[]>>;
  goalGroupSorting?: {
    lifeArea?: {
      sorting: Record<ID, ID[]>;
    };
  };
  taskCounts?: Map<ID, number>;
  goalProgressCounts?: Map<ID, GoalProgressCount>;
  index?: GoalTreeIndex<TGoal>;
  links: EntityLinks;
};

export const buildSortedGoalTree = <
  TGoal extends Goal,
  TLifeArea extends LifeArea,
>({
  goals,
  sortedLifeAreas,
  goalGroupBy,
  goalSortingMode,
  goalRoadmapSorting,
  goalGroupSorting,
  taskCounts,
  goalProgressCounts,
  index,
  links,
}: BuildSortedGoalTreeOptions<TGoal, TLifeArea>): TGoal[] => {
  if (!goals.length) {
    return [];
  }

  const rootGoals = goals.filter((goal) => !hasGoalParent(goal, { links }));

  const customOrder =
    goalRoadmapSorting?.[GoalRoadmapSortingKeys.InProgress] ?? [];

  // An archived or frozen life area is not a group; its goals fall to the no-life-area bucket.
  const activeLifeAreas = sortedLifeAreas.filter(isActiveLifeArea);
  const validLifeAreaIds = new Set(activeLifeAreas.map((la) => la.id));
  const lifeAreaOrder = new Map(
    activeLifeAreas.map((la, index) => [la.id, index]),
  );

  const sortMode = goalSortingMode ?? GoalSortingMode.StartDate;

  const sortedRootGoals =
    goalGroupBy === GroupByMode.LifeArea
      ? [
          ...activeLifeAreas
            .map((lifeArea) =>
              sortGoals(
                rootGoals.filter(
                  ({ lifeAreaId }) => lifeAreaId === lifeArea.id,
                ),
                {
                  sortMode,
                  allGoals: goals,
                  customOrder: lifeArea.goalSorting ?? [],
                  taskCounts,
                  goalProgressCounts,
                  links,
                },
              ),
            )
            .flat(),
          ...sortGoals(
            rootGoals.filter(
              ({ lifeAreaId }) =>
                !lifeAreaId || !validLifeAreaIds.has(lifeAreaId),
            ),
            {
              allGoals: goals,
              sortMode,
              customOrder: goalGroupSorting?.lifeArea?.sorting?.['null'] ?? [],
              taskCounts,
              goalProgressCounts,
              links,
            },
          ),
        ]
      : sortGoals(rootGoals, {
          allGoals: goals,
          sortMode,
          customOrder,
          lifeAreaOrder,
          taskCounts,
          goalProgressCounts,
          links,
        });

  // Built once — the previous per-node `goals.filter` made this O(n^2).
  const childrenByParent =
    index?.childrenByParentId ?? buildParentToChildrenMap(goals, links);

  // `visited` guards against a corrupt parent cycle sending the walk infinite; the depth cap guards
  // a chain too deep for the stack.
  const visited = new Set<ID>();

  const processGoals = (parentGoals: TGoal[], depth: number): TGoal[] =>
    parentGoals.flatMap((parentGoal) => {
      if (visited.has(parentGoal.id) || depth >= MAX_GOAL_DEPTH) {
        return [];
      }
      visited.add(parentGoal.id);

      // The index's arrays outlive this call, so hand sortGoals a copy.
      const childGoals = [...(childrenByParent.get(parentGoal.id) ?? [])];

      const sortedChildGoals = sortGoals(childGoals, {
        sortMode,
        customOrder: parentGoal.goalSorting ?? [],
        allGoals: goals,
        taskCounts,
        goalProgressCounts,
        links,
      });

      return [parentGoal, ...processGoals(sortedChildGoals, depth + 1)];
    });

  return processGoals(sortedRootGoals, 0);
};
